/* Footer Accordion for link_list blocks
 * - Turns footer menus into accordions at <= 749px
 * - Accessible: toggles aria-expanded, keyboard support
 * - Respects desktop: removes inline styles and restores open state
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function FooterAccordion(root) {
    this.root = root; // footer element
    this.blocks = [];
    this.enabled = false;
    this._onResize = debounce(this._handleResize.bind(this), 150);
    this._initBlocks();
    this._handleResize(); // enable/disable on load
    window.addEventListener('resize', this._onResize);
  }

  FooterAccordion.prototype.destroy = function () {
    this._disable();
    window.removeEventListener('resize', this._onResize);
  };

  FooterAccordion.prototype._initBlocks = function () {
    var blockNodes = this.root.querySelectorAll('.footer-block.footer-block--menu, .footer-block.footer-block--menu-mobile');
    for (var i = 0; i < blockNodes.length; i++) {
      var block = blockNodes[i];
      var heading = block.querySelector('.footer-block__heading--toggle');
      if (!heading) continue;
      var controlsId = heading.getAttribute('aria-controls');
      var content = controlsId ? document.getElementById(controlsId) : null;
      if (!content) continue;
      this.blocks.push({ block: block, heading: heading, content: content, handler: null, keyHandler: null, onTransitionEnd: null });
    }
  };

  FooterAccordion.prototype._enable = function () {
    if (this.enabled) return;
    this.enabled = true;
    this.root.classList.add('footer-accordion--enabled');

    for (var i = 0; i < this.blocks.length; i++) {
      var b = this.blocks[i];
      // Collapse by default on mobile
      b.heading.setAttribute('aria-expanded', 'false');
      b.block.classList.remove('is-open');
      b.content.style.maxHeight = '0px';
      b.content.style.overflow = 'hidden';

      // Click handler
      b.handler = this._toggle.bind(this, b);
      b.heading.addEventListener('click', b.handler);
      // Keyboard (Enter/Space)
      b.keyHandler = function (evt) {
        if (evt.key === 'Enter' || evt.key === ' ' || evt.keyCode === 13 || evt.keyCode === 32) {
          evt.preventDefault();
          this.click();
        }
      }.bind(b.heading);
      b.heading.addEventListener('keydown', b.keyHandler);
    }

    // Update newsletter button text on mobile
    this._setNewsletterText();
  };

  FooterAccordion.prototype._disable = function () {
    if (!this.enabled) return;
    this.enabled = false;
    this.root.classList.remove('footer-accordion--enabled');

    for (var i = 0; i < this.blocks.length; i++) {
      var b = this.blocks[i];
      // Remove listeners
      if (b.handler) b.heading.removeEventListener('click', b.handler);
      if (b.keyHandler) b.heading.removeEventListener('keydown', b.keyHandler);
      if (b.onTransitionEnd) b.content.removeEventListener('transitionend', b.onTransitionEnd);
      b.handler = null;
      b.keyHandler = null;
      b.onTransitionEnd = null;
      // Reset styles and ARIA for desktop
      b.heading.setAttribute('aria-expanded', 'true');
      b.content.style.maxHeight = '';
      b.content.style.overflow = '';
      b.block.classList.remove('is-open');
    }

    // Restore newsletter button text when leaving mobile
    this._restoreNewsletterText();
  };

  FooterAccordion.prototype._expand = function (b) {
    // Ensure we can animate from 0 to scrollHeight
    // If currently collapsed, maxHeight is '0px'
    b.content.style.overflow = 'hidden';
    // Set to measured height
    var target = b.content.scrollHeight;
    b.content.style.maxHeight = target + 'px';

    // After transition completes, set to none to allow content growth without clipping
    if (b.onTransitionEnd) b.content.removeEventListener('transitionend', b.onTransitionEnd);
    b.onTransitionEnd = function (evt) {
      if (evt.propertyName !== 'max-height') return;
      if (b.heading.getAttribute('aria-expanded') === 'true') {
        b.content.style.maxHeight = 'none';
      }
      b.content.removeEventListener('transitionend', b.onTransitionEnd);
      b.onTransitionEnd = null;
    };
    b.content.addEventListener('transitionend', b.onTransitionEnd);
  };

  FooterAccordion.prototype._collapse = function (b) {
    // If maxHeight is 'none' or empty, set it to current height first for smooth animation
    var mh = b.content.style.maxHeight;
    if (!mh || mh === 'none') {
      var current = b.content.scrollHeight;
      b.content.style.maxHeight = current + 'px';
      // force reflow
      void b.content.offsetHeight;
    }
    // Then go to 0 to animate
    b.content.style.maxHeight = '0px';
  };

  FooterAccordion.prototype._toggle = function (b) {
    var expanded = b.heading.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      b.heading.setAttribute('aria-expanded', 'false');
      b.block.classList.remove('is-open');
      this._collapse(b);
    } else {
      b.heading.setAttribute('aria-expanded', 'true');
      b.block.classList.add('is-open');
      // If previously set to none, reset to 0 first to animate open
      if (b.content.style.maxHeight === 'none') {
        b.content.style.maxHeight = '0px';
        // force reflow
        void b.content.offsetHeight;
      }
      this._expand(b);
    }
  };

  FooterAccordion.prototype._handleResize = function () {
    if (isMobile()) this._enable(); else this._disable();
  };

  // Set newsletter button text to 'Subscribe' on mobile, store original text
  FooterAccordion.prototype._setNewsletterText = function () {
    var buttons = this.root.querySelectorAll('.newsletter-form__button');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (!btn) continue;
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent || '';
      }
      // Set visible text to 'Subscribe' per requirement
      btn.textContent = 'Subscribe';
    }
  };

  // Restore original newsletter button text when leaving mobile
  FooterAccordion.prototype._restoreNewsletterText = function () {
    var buttons = this.root.querySelectorAll('.newsletter-form__button');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (!btn) continue;
      if (btn.dataset && typeof btn.dataset.originalText !== 'undefined') {
        btn.textContent = btn.dataset.originalText;
      }
    }
  };

  function initInContainer(container) {
    var footers = container ? container.querySelectorAll('footer.footer') : document.querySelectorAll('footer.footer');
    for (var i = 0; i < footers.length; i++) {
      var footer = footers[i];
      // Attach only once
      if (!footer.__footerAccordionInstance) {
        footer.__footerAccordionInstance = new FooterAccordion(footer);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initInContainer(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    if (evt && evt.target) {
      initInContainer(evt.target);
    }
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    if (evt && evt.target) {
      var footers = evt.target.querySelectorAll('footer.footer');
      for (var i = 0; i < footers.length; i++) {
        var inst = footers[i].__footerAccordionInstance;
        if (inst) {
          inst.destroy();
          delete footers[i].__footerAccordionInstance;
        }
      }
    }
  });
})();
